/*
 * Mapping Admin API
 *
 * API functions for managing data mapping administration
 */

import config from '../config';
import { authFetch } from '../common/authentication';
import {
  FieldMappingDataRow,
  FixingProxyMappingDataRow,
  LegalEntityMappingDataRow,
  AddedRows,
  EditedRows,
  DeletedRows,
  MappingDataValues,
} from '../common/domain/mappingAdmin';

/**
 * API call to get mapping data
 * @param mappingType - The type of mapping data to retrieve
 * @returns Promise resolving to mapping data array
 */
export const getMappingData = async (
  mappingType: string,
): Promise<
  | FieldMappingDataRow[]
  | FixingProxyMappingDataRow[]
  | LegalEntityMappingDataRow[]
> => {
  const response = await authFetch(
    `${config.endpoints.mappingAdmin}?mappingType=${encodeURIComponent(
      mappingType,
    )}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.json();
};

/**
 * API call to post mapping data changes
 * @param mappingType - The type of mapping data to update
 * @param addedRows - New rows to be added
 * @param editedRows - Existing rows with modifications
 * @param deletedRows - Rows to be deleted
 * @returns Promise
 */
export const postMappingData = async (
  mappingType: string,
  addedRows: AddedRows,
  editedRows: EditedRows,
  deletedRows: DeletedRows,
): Promise<any> => {
  const response = await authFetch(config.endpoints.mappingAdmin, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      mappingType,
      addedRows,
      editedRows,
      deletedRows,
    }),
  });
  return response.json();
};

/**
 * API call to get accepted mapping data values
 * @returns Promise resolving to mapping data values
 */
export const getMappingDataValues = async (): Promise<MappingDataValues> => {
  const response = await authFetch(config.endpoints.mappingAdminValues, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.json();
};
